// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // During server-side rendering there is no `window`, and Node's global fetch
  // cannot parse relative URLs. Have the server call itself on its own port.
  // NOTE: this file is compiled into the bundle, so `process.env.PORT` is NOT
  // readable at SSR runtime (only REACT_APP_* + NODE_ENV are inlined at build).
  // REACT_APP_SSR_PORT is baked in at build time (8080 on App Runner) so the
  // self-call hits the real listen port instead of defaulting to 3000.
  if (typeof window === 'undefined') {
    const serverPort = process.env.REACT_APP_SSR_PORT || process.env.PORT || 3000;
    return `http://localhost:${serverPort}`;
  }

  return '';
};

export const fetchDynamoDBListingByIdAPI = listingId => {
  const path = `/api/dynamodb/listings/${listingId}`;

  return get(path, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const fetchDynamoDBListingsAPI = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Add search parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  const path = `/api/dynamodb/listings${queryString ? `?${queryString}` : ''}`;

  return get(path, {
    headers: { 'Content-Type': 'application/json' }, // Override default transit header for DynamoDB API
  });
};

// DynamoDB Health Check
export const checkDynamoDBHealthAPI = () => {
  return get('/api/dynamodb/health', {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Helper function specifically for search page integration
export const searchDynamoDBListings = searchParams => {
  const params = {};

  // Map search parameters to API parameters
  if (searchParams.keywords) {
    params.keywords = searchParams.keywords;
  }

  if (searchParams.bounds) {
    params.bounds =
      typeof searchParams.bounds === 'object'
        ? JSON.stringify(searchParams.bounds)
        : searchParams.bounds;
  }

  if (searchParams.price) {
    params.price = searchParams.price;
  }

  if (searchParams.page) {
    params.page = searchParams.page;
  }

  if (searchParams.perPage) {
    params.perPage = searchParams.perPage;
  }

  if (searchParams.sort) {
    params.sort = searchParams.sort;
  }

  if (searchParams.destination) {
    params.destination = searchParams.destination;
  }

  return fetchDynamoDBListingsAPI(params);
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  // If Content-Type is application/json, serialize as JSON instead.
  const shouldSerializeBody = body && (!headers || headers['Content-Type'] === 'application/transit+json');
  const shouldSerializeAsJson = body && headers && headers['Content-Type'] === 'application/json';
  
  let bodyMaybe = {};
  if (shouldSerializeBody) {
    bodyMaybe = { body: serialize(body) };
  } else if (shouldSerializeAsJson) {
    bodyMaybe = { body: JSON.stringify(body) };
  }

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // Use a universal fetch reference so requests work both in the browser and
  // during server-side rendering (where `window` is undefined). Node 22+ and
  // modern browsers both expose `fetch` on globalThis.
  const fetchFn = typeof window !== 'undefined' ? window.fetch.bind(window) : globalThis.fetch;
  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Tell the server that a transaction was just accepted by the host so the
// renter's membership day-counter can increment. Idempotent on the server,
// so safe to retry. Used by the non-instant-book accept flow; the
// instant-book path increments the counter server-side already.
export const membershipBookingApplied = transactionId => {
  return post('/api/membership/booking-applied', { transactionId });
};

// Symmetric release call — fired after decline / operator-decline / cancel
// so the renter's day counter decrements and the cap reflects only bookings
// that actually happened. Idempotent on the server.
export const membershipBookingReleased = (transactionId, reason) => {
  return post('/api/membership/booking-released', { transactionId, reason });
};

// Step 1 of the membership purchase: create a Stripe PaymentIntent. Returns
// { clientSecret, paymentIntentId } that the client confirms via Stripe.js.
export const membershipPurchaseIntent = body => {
  return post('/api/membership/purchase/intent', body);
};

// Step 2: after stripe.confirmPayment succeeds client-side, call this so the
// server verifies status via Stripe directly and writes the membership to
// user publicData. Idempotent — safe to retry if the network blips.
export const membershipPurchaseComplete = paymentIntentId => {
  return post('/api/membership/purchase/complete', { paymentIntentId });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Stripe Identity
export const createVeriffSessionAPI = body => {
  return post('/api/stripe-identity/create-session', body);
};

export const getVeriffSessionAPI = (sessionId, transactionId) => {
  return get(`/api/stripe-identity/get-session/${sessionId}/${transactionId}`);
};

// S3
export const uploadImageToS3API = '/api/s3/uploadToS3';
export const uploadDocumentToS3API = '/api/s3/documents/upload';

// Sendgrid
export const sendDamageDepositEmailAPI = `/api/sendgrid/send-damage-deposit-email`;

// Transactions
export const updateTransactionMetadataAPI = body => {
  return post('/api/transactions/update-metadata', body);
};

export const acceptCancellationRequestAPI = body =>
  post('/api/transactions/accept-cancellation', body);

export const acceptDateChangeRequestAPI = body =>
  post('/api/transactions/accept-date-change', body);

export const notifyTransactionRequestAPI = body =>
  post('/api/transactions/notify-request', body);

// Host payments ledger
export const paymentsSummaryAPI = () =>
  get('/api/payments/summary', { headers: { 'Content-Type': 'application/json' } });

export const paymentsSyncLedgerAPI = () =>
  post(
    '/api/payments/sync-ledger',
    {},
    { headers: { 'Content-Type': 'application/json' } }
  );

export const paymentsTriggerPayoutAPI = body =>
  post('/api/payments/trigger-payout', body || {}, {
    headers: { 'Content-Type': 'application/json' },
  });

export const approveListingAPI = `${apiBaseUrl()}/api/listings/approve`;

// Slack Support Chat APIs
export const createSupportChannelAPI = body => {
  return post('/api/slack/support/create-channel', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const sendMessageToSlackAPI = body => {
  return post('/api/slack/support/send-message', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getChannelMessagesAPI = channelId => {
  return get(`/api/slack/support/messages?channelId=${channelId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const closeSupportChannelAPI = body => {
  return post('/api/slack/support/close-channel', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getChatSessionAPI = userId => {
  return get(`/api/slack/support/sessions/${userId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const closeChatSessionAPI = body => {
  return post('/api/slack/support/sessions/close', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getUserSupportChatsAPI = userId => {
  return get(`/api/slack/support/user-chats/${userId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// RentalTide Marketplace
export const fetchRentalTideListingsAPI = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });
  const queryString = queryParams.toString();
  const path = `/api/rentaltide/listings${queryString ? `?${queryString}` : ''}`;
  return get(path, { headers: { 'Content-Type': 'application/json' } });
};

export const searchRentalTideListings = searchParams => {
  const params = {};
  if (searchParams.bounds) {
    // RT expects: NE_lat,NE_lng,SW_lat,SW_lng
    if (typeof searchParams.bounds === 'object') {
      const b = searchParams.bounds;
      params.bounds = `${b.ne.lat},${b.ne.lng},${b.sw.lat},${b.sw.lng}`;
    } else {
      params.bounds = searchParams.bounds;
    }
  }
  if (searchParams.category) params.category = searchParams.category;
  if (searchParams.perPage) params.limit = searchParams.perPage;
  if (searchParams.page && searchParams.perPage) {
    params.offset = (searchParams.page - 1) * searchParams.perPage;
  }
  return fetchRentalTideListingsAPI(params);
};

// AI Search
export const aiSearchQueryAPI = body => {
  return post('/api/ai-search/query', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Delays the promise
 *
 * @param {number} ms
 */
export const delayPromise = ms => new Promise(resolve => setTimeout(resolve, ms));
